import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  ModalBody,
  ModalFooter,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ModalBox } from "../ModalBox";
import { AddIcon } from "@chakra-ui/icons";
import { FormSelect } from "../FormSelect";
import { FormInput } from "../FormInput";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  MY_CLASSES,
  ORG_CLASSES,
  ORG_GRADES_SUBJECTS,
} from "../../graphql/queries";
import {
  CREATE_CLASS,
  UPDATE_CLASS,
} from "../../graphql/mutations";
import { useHistory } from "react-router-dom";
import { RadioTree } from "../RadioTree";
import { generateString } from "../../utils/functions";

export const AddToDynoClass = ({
  isOpen,
  onClose,
  dynoToAdd,
  selectedGradeName,
  selectedSubjectName,
  domainName,
  user,
  updateDynoStatus,
}) => {
  const [classes, setClasses] = useState([]);
  const [dynoToAddInModule, setDynoToAddInModules] = useState({});
  const [orgGradesSubjects, setOrgGradesSubjects] = useState({
    orgGrades: [],
    orgSubjects: [],
  });
  const [newOrgClass, setNwOrgClass] = useState({
    name: "",
    grade: "",
    subject: "",
  });
  const [isAdding, setIsAdding] = useState(false);
  const [isCreateClass, setIsCreateClass] = useState(false);
  const history = useHistory();
  const toast = useToast();

  const showToast = (title, status) => {
    toast({
      position: "top",
      title,
      status,
      duration: 9000,
      isClosable: true,
    });
  };

  const [getOrgClasses] = useLazyQuery(ORG_CLASSES, {
    onCompleted: (data) => {
      const orgClasses = data.organizationClasses
        .map((oc) => ({
          id: oc.id,
          label: oc.name,
          children: oc.modules.map((m) => ({
            id: m.id,
            label: m.moduleName,
            children: [],
          })),
        }))
        .filter((oc) => oc.children.length > 0);
      setClasses(orgClasses);
      setIsCreateClass(orgClasses.length === 0);
    },
    onError: () => {},
    fetchPolicy: "cache-and-network",
  });

  const [getOrgGradesSubjects] = useLazyQuery(ORG_GRADES_SUBJECTS, {
    onCompleted: (data) => {
      setOrgGradesSubjects({
        orgGrades: data.organizationGrades,
        orgSubjects: data.organizationSubjects,
      });
      setNwOrgClass({
        ...newOrgClass,
        name: selectedSubjectName
          ? `${selectedGradeName}-${selectedSubjectName}`
          : "",
        grade: data.organizationGrades.find(
          (og) => og.gradeName === selectedGradeName
        )?.id,
        subject: data.organizationSubjects.find(
          (os) => os.subjectName === selectedSubjectName
        )?.id,
      });
    },
    onError: () => {},
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const [createOrgClass] = useMutation(CREATE_CLASS, {
    update: (cache, { data }) => {
      const cached_class = cache.readQuery({
        query: MY_CLASSES,
        variables: {
          where: {
            OR: [
              {
                primaryCreator: {
                  id: {
                    equals: user.id,
                  },
                },
              },
              {
                creator: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
              {
                subscribers: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
            ],
          },
        },
      });
      cache.writeQuery({
        query: MY_CLASSES,
        variables: {
          where: {
            OR: [
              {
                primaryCreator: {
                  id: {
                    equals: user.id,
                  },
                },
              },
              {
                creator: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
              {
                subscribers: {
                  some: {
                    id: {
                      equals: user.id,
                    },
                  },
                },
              },
            ],
          },
        },
        data: {
          organizationClasses: [
            ...cached_class.organizationClasses,
            data.createOrganizationClass,
          ],
        },
      });
      setIsAdding(false);
      onModalClose();
      updateDynoStatus();
      showToast("Created class and added the dyno.", "success");
      history.push(`class/${data.createOrganizationClass.id}`);
    },
    onError: () => {
      showToast("Failed to create class try again.", "error");
      setIsAdding(false);
      onModalClose();
    },
  });

  const [updateOrgClass] = useMutation(UPDATE_CLASS, {
    onCompleted: (data) => {
      setIsAdding(false);
      onModalClose();
      updateDynoStatus();
      showToast("Added the dyno to class.", "success");
    },
    onError: () => {
      showToast("Failed to add dyno to class try again.", "error");
      setIsAdding(false);
      onModalClose();
    },
  });

  useEffect(() => {
    if (user.id) {
      getOrgClasses({
        variables: {
          where: {
            primaryCreator: {
              id: {
                equals: user.id,
              },
            },
          },
        },
      });
    }
  }, [user.id, getOrgClasses]);

  useEffect(() => {
    getOrgGradesSubjects({
      variables: {
        gradeWhere: {
          organizationId: {
            equals: null,
          },
        },
        subjectWhere: {
          organizationId: {
            equals: null,
          },
        },
      },
    });
  }, [getOrgGradesSubjects]);

  useEffect(() => {
    if (
      orgGradesSubjects.orgGrades.length > 0 &&
      orgGradesSubjects.orgSubjects.length > 0
    ) {
      setNwOrgClass({
        ...newOrgClass,
        name: `${selectedGradeName}-${selectedSubjectName}`,
        grade: orgGradesSubjects.orgGrades.find(
          (og) => og.gradeName === selectedGradeName
        )?.id,
        subject: orgGradesSubjects.orgSubjects.find(
          (os) => os.subjectName === selectedSubjectName
        )?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGradeName, selectedSubjectName]);

  const handleNodeSelect = (treeId, nodeId) => {
    console.log("Selected:", treeId, nodeId);
    setDynoToAddInModules({ id: treeId, moduleId: nodeId });
    // Add your logic here to handle the selected tree ID and node ID
  };

  const onModalClose = () => {
    setDynoToAddInModules({});
    setIsCreateClass(classes.length === 0);
    onClose();
  };

  const addDynoToClass = () => {
    setIsAdding(true);
    updateOrgClass({
      variables: {
        where: {
          id: dynoToAddInModule.id,
        },
        data: {
          modules: {
            update: [
              {
                where: {
                  id: dynoToAddInModule.moduleId,
                },
                data: {
                  dynosUnderModule: {
                    connect: [
                      {
                        id: dynoToAdd,
                      },
                    ],
                  },
                },
              },
            ],
          },
        },
      },
    });
  };

  const createNewClass = () => {
    setIsCreateClass(!isCreateClass);
  };

  const onChange = (e) => {
    setNwOrgClass({
      ...newOrgClass,
      [e.target.name]: e.target.value,
    });
  };

  const addDynoAndCreateOrgClass = (e) => {
    e.preventDefault();
    setIsAdding(true);
    createOrgClass({
      variables: {
        data: {
          name: newOrgClass.name,
          classCode: generateString(6),
          grade: {
            connect: {
              id: newOrgClass.grade,
            },
          },
          subject: {
            connect: {
              id: newOrgClass.subject,
            },
          },
          modules: {
            create: [
              {
                moduleName: domainName,
                dynosUnderModule: {
                  connect: [
                    {
                      id: dynoToAdd,
                    },
                  ],
                },
              },
            ],
          },
          primaryCreator: {
            connect: {
              id: user.id,
            },
          },
          creator: {
            connect: [
              {
                id: user.id,
              },
            ],
          },
        },
      },
    });
  };

  return (
    <ModalBox
      title="Add dyno to class"
      isOpen={isOpen}
      modalProps={{
        closeOnOverlayClick: false,
      }}
      onClose={onModalClose}
    >
      {!isCreateClass ? (
        <ClassList
          classes={classes}
          handleNodeSelect={handleNodeSelect}
          dynoToAddInModule={dynoToAddInModule}
          isAdding={isAdding}
          addDynoToClass={addDynoToClass}
          onCancel={onModalClose}
          createNewClass={createNewClass}
        />
      ) : (
        <CreateNewClass
          name={newOrgClass.name}
          grade={newOrgClass.grade}
          subject={newOrgClass.subject}
          orgGrades={orgGradesSubjects.orgGrades}
          orgSubjects={orgGradesSubjects.orgSubjects}
          onChange={onChange}
          createClass={addDynoAndCreateOrgClass}
          isAdding={isAdding}
          onCancel={onModalClose}
        />
      )}
    </ModalBox>
  );
};

const ClassList = ({
  classes,
  handleNodeSelect,
  dynoToAddInModule,
  isAdding,
  addDynoToClass,
  onCancel,
  createNewClass,
}) => {
  return (
    <>
      <ModalBody>
        <Flex
          style={{
            justifyContent: "flex-end",
            margin: "10px 0 20px",
          }}
        >
          <Button
            variant="outline"
            colorScheme="brand"
            onClick={createNewClass}
          >
            <AddIcon fontSize="16px" marginRight="5px" /> Create new class
          </Button>
        </Flex>
        {classes.length > 0 ? (
          <RadioTree treeData={classes} onNodeSelect={handleNodeSelect} />
        ) : (
          <Text
            style={{
              textAlign: "center",
              margin: "10px 0",
              fontSize: "16px",
            }}
          >
            No classes!
          </Text>
        )}
      </ModalBody>
      <ModalFooter borderTop="1px solid #eee">
        <Flex
          style={{
            justifyContent: "flex-end",
          }}
        >
          <Button
            colorScheme="brand"
            _hover={{
              bg: "blue.500",
            }}
            style={{
              margin: "0 10px 0 0",
            }}
            isDisabled={!dynoToAddInModule.id}
            isLoading={isAdding}
            loadingText="Adding"
            onClick={addDynoToClass}
          >
            Add
          </Button>
          <Button
            variant="outline"
            colorScheme="red"
            isDisabled={isAdding}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Flex>
      </ModalFooter>
    </>
  );
};

const CreateNewClass = ({
  name,
  grade,
  subject,
  orgGrades,
  orgSubjects,
  onChange,
  createClass,
  isAdding,
  onCancel,
}) => {
  return (
    <>
      <ModalBody>
        <form onSubmit={createClass}>
          <FormInput
            label="Name"
            name="name"
            type="text"
            placeholder=""
            inputOnChange={onChange}
            value={name}
            formProps={{ isRequired: true }}
            padding="0 0 20px"
          />
          <FormSelect
            formProps={{ isRequired: false }}
            padding={{ base: "0 0 10px 0", md: "0 0 20px 0" }}
            label="Grade"
            options={orgGrades.map((og) => ({
              ...og,
              value: og.id,
              name: og.gradeName,
            }))}
            value={grade}
            placeholder="Select grade"
            selectOnChange={onChange}
            name="grade"
          />
          <FormSelect
            formProps={{ isRequired: false }}
            padding={{ base: "0 0 10px 0", md: "0 0 20px 0" }}
            label="Subject"
            options={orgSubjects.map((os) => ({
              ...os,
              value: os.id,
              name: os.subjectName,
            }))}
            value={subject}
            placeholder="Select subject"
            selectOnChange={onChange}
            name="subject"
          />
          <Flex
            style={{
              justifyContent: "flex-end",
              margin: "0 0 16px",
            }}
          >
            <Button
              type="submit"
              isLoading={isAdding}
              loadingText="Updating"
              colorScheme="brand"
              _hover={{
                bg: "blue.500",
              }}
              style={{
                margin: "0 10px 0 0",
              }}
            >
              Create and add to class
            </Button>
            <Button
              variant="outline"
              colorScheme="red"
              isDisabled={isAdding}
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Flex>
        </form>
      </ModalBody>
    </>
  );
};
